// Route table. /login and /auth/callback are public; everything else needs a
// session and renders inside the AppShell (top bar + side nav).
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { RequireAuth } from "./auth/RequireAuth";
import { AppShell } from "./components/AppShell";
import { BillingPage } from "./pages/BillingPage";
import { ProfilePage } from "./pages/ProfilePage";
import { ConfirmEmailPage } from "./pages/ConfirmEmailPage";
import { AuthCallbackPage } from "./pages/AuthCallbackPage";
import { AnalyticsPage } from "./pages/AnalyticsPage";
import { Dashboard } from "./pages/Dashboard";
import { LeadsPage } from "./pages/LeadsPage";
import { LoginPage } from "./pages/LoginPage";
import { InvitePage } from "./pages/InvitePage";
import { UsersPage } from "./pages/UsersPage";
import { Placeholder } from "./pages/Placeholder";
import { BrandingPage } from "./pages/widgets/BrandingPage";
import { HostedPage } from "./pages/widgets/HostedPage";
import { InstallPage } from "./pages/widgets/InstallPage";
import { NewWidgetPage } from "./pages/widgets/NewWidgetPage";
import { NotificationsPage } from "./pages/widgets/NotificationsPage";
import { ContactFormPage } from "./pages/widgets/ContactFormPage";
import { PricingTab } from "./pages/widgets/SolarPricingPage";
import { WidgetLayout } from "./pages/widgets/WidgetLayout";
import { WidgetsPage } from "./pages/widgets/WidgetsPage";
import { navItems } from "./sections";
import { CataloguePage } from "./pages/quotes/CataloguePage";
import { NewQuotePage } from "./pages/quotes/NewQuotePage";
import { ProposalPage } from "./pages/quotes/ProposalPage";
import { QuoteBuilderPage } from "./pages/quotes/QuoteBuilderPage";
import { QuoteSettingsPage } from "./pages/quotes/QuoteSettingsPage";
import { QuotesPage } from "./pages/quotes/QuotesPage";
import { QuoteGate } from "./quotes/Access";

/** An old address (/tools/…, /widgets/…): the same page under /components/…, query string kept. */
function RenamedPath({ from }: { from: string }) {
  const { pathname, search } = useLocation();
  return <Navigate to={`/components${pathname.slice(from.length)}${search}`} replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      {/* The link in a notification-address confirmation email (no sign-in) */}
      <Route path="/confirm-email" element={<ConfirmEmailPage />} />
      {/* A customer's quote (go.<domain>/q/<token>): no sign-in, the token is the key */}
      <Route path="/q/:token" element={<ProposalPage />} />
      {/* The link in an invite email (no sign-in): set a password and join the company */}
      <Route path="/invite" element={<InvitePage />} />

      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="leads" element={<LeadsPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="users" element={<UsersPage />} />

        {/* Website components: the list, then one with Branding / Pricing / Install tabs */}
        <Route path="components" element={<WidgetsPage />} />
        <Route path="components/new" element={<NewWidgetPage />} />
        <Route path="components/:widgetId" element={<WidgetLayout />}>
          <Route index element={<Navigate to="branding" replace />} />
          <Route path="branding" element={<BrandingPage />} />
          {/* Heat pump sizes, or system and inverter prices for a solar estimate */}
          <Route path="pricing" element={<PricingTab />} />
          <Route path="form" element={<ContactFormPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="hosted" element={<HostedPage />} />
          <Route path="install" element={<InstallPage />} />
        </Route>

        {/* Quotes: the list, a new quote, one quote, the catalogue and settings */}
        {/* Every Quotes page is the QuoteFlow plan: QuoteGate explains it to companies without it */}
        <Route path="quotes" element={<QuoteGate><QuotesPage /></QuoteGate>} />
        <Route path="quotes/new" element={<QuoteGate><NewQuotePage /></QuoteGate>} />
        <Route path="quotes/catalogue" element={<QuoteGate><CataloguePage /></QuoteGate>} />
        <Route path="quotes/settings" element={<QuoteGate><QuoteSettingsPage /></QuoteGate>} />
        <Route path="quotes/:quoteId" element={<QuoteGate><QuoteBuilderPage /></QuoteGate>} />

        {/* Old links: /tools/… (before the rename to website components) and /widgets/… (before that) */}
        <Route path="tools/*" element={<RenamedPath from="/tools" />} />
        <Route path="widgets/*" element={<RenamedPath from="/widgets" />} />

        {/* Sections still to be built ("soon" items have no route) */}
        {navItems
          .filter((i) => !i.custom && !i.soon)
          .map((i) => (
            <Route key={i.path} path={i.path} element={<Placeholder title={i.label} blurb={i.blurb} />} />
          ))}
        {/* Unknown paths go back to the dashboard */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
}
